import { cn } from '@invent/wl-ui-kit-next';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@invent/wl-ui-kit-next/ui';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';

import { ChoiceSelect } from '@/design';
import { useCan } from '@/domains/auth';
import { mailTemplatesApi, type IMailRenderOptions } from '../../api/mail-templates-api';
import type {
  IMailBody,
  IMailCondition,
  IMailPreview,
  IMailSample,
  IMailTemplate,
  MailPreviewRole,
  MailSurvey
} from '../../api/mail-template-contracts';
import { TableSkeleton } from '../../components/admin-page';
import { refusalMessage } from '../../refusal';
import { SamplePicker } from './sample-picker';
import { conditionLabel } from './rich-part';
import { isInvalidBody, problemsOf, type FieldProblems } from './template-fields';

/**
 * The preview survey-api renders exactly as the worker sends it: who the copy is for, whether the
 * PDF is attached, a desktop or a phone's width, the HTML or the plain-text part, filled with the
 * placeholders' samples, with every value empty, or with a submitted response's; the template's
 * conditions follow the recipient and the PDF, or are each turned on or off by hand. Above it the
 * inbox's row: sender, subject and preview text. In the editor its
 * marked regions (`data-part`, preview only) are linked to the fields: the focused field's region
 * is outlined, and a click on a region focuses its field — on a portal block (`data-system-block`),
 * selects its card in the body. The frame runs no script —
 * `allow-same-origin` only lets this page reach into its document for that.
 */

export interface IPreviewOptions {
  role: MailPreviewRole;
  attached: boolean;
  device: 'desktop' | 'mobile';
  format: 'html' | 'text';
  /** Every value empty, so the fallbacks show. */
  empty: boolean;
  /** The submitted response the values come from; null for the samples. */
  sample: Nullable<IMailSample>;
  /** The conditions turned on by hand; null follows the recipient and the PDF. */
  conditions: Nullable<readonly string[]>;
}

export const PREVIEW_DEFAULTS: IPreviewOptions = {
  role: 'submitter',
  attached: true,
  device: 'desktop',
  format: 'html',
  empty: false,
  sample: null,
  conditions: null
};

/** What a render request carries of the options. */
export function renderOptions(options: IPreviewOptions, survey: Nullable<MailSurvey>): IMailRenderOptions {
  return {
    survey,
    role: options.role,
    attached: options.attached,
    empty: options.empty,
    responseId: options.sample?.responseId ?? null,
    participantId: options.sample?.participantId ?? null,
    conditions: options.conditions
  };
}

/** The recipient's conditions (`is_submitter`, `is_advisor`, `is_cc`). */
const ROLE_CONDITIONS: Record<MailPreviewRole, string> = {
  submitter: 'is_submitter',
  advisor: 'is_advisor',
  cc: 'is_cc'
};

/** Which conditions hold in the preview: those turned on by hand, or — following the recipient and
 *  the PDF — the role's, `pdf_attached` with the PDF, and every other at its sample. */
export function conditionsOn(conditions: readonly IMailCondition[], options: IPreviewOptions): Set<string> {
  if (options.conditions) return new Set(options.conditions);
  const roles = Object.values(ROLE_CONDITIONS);
  return new Set(
    conditions
      .filter(({ name, sample }) => {
        if (roles.includes(name)) return ROLE_CONDITIONS[options.role] === name;
        if (name === 'pdf_attached') return options.attached;
        return sample;
      })
      .map(({ name }) => name)
  );
}

const PREVIEW_ROLES: readonly { value: MailPreviewRole; label: string }[] = [
  { value: 'submitter', label: 'Who submitted' },
  { value: 'advisor', label: 'The advisor, submitted for them' },
  { value: 'cc', label: 'The CC address' }
];

export interface IPreviewState {
  /** The last preview survey-api rendered — kept, dimmed, while the fields have problems. */
  shown: Nullable<IMailPreview>;
  problems: FieldProblems;
  /** A refusal that names no field, or a preview that failed outright. */
  other: Nullable<string>;
  pending: boolean;
}

/** Previews `body` on every change; the cleanup aborts the stale request, so an older answer
 *  never lands over a newer one. */
export function usePreview(
  code: string,
  survey: Nullable<MailSurvey>,
  body: IMailBody,
  keys: readonly string[],
  options: IPreviewOptions
): IPreviewState {
  const { role, attached, empty, responseId, participantId } = renderOptions(options, survey);
  const conditions = options.conditions?.join(',') ?? null;
  const [state, setState] = React.useState<IPreviewState>({
    shown: null,
    problems: {},
    other: null,
    pending: true
  });
  React.useEffect(() => {
    const controller = new AbortController();
    setState((now) => ({ ...now, pending: true }));
    const asked = {
      survey,
      role,
      attached,
      empty,
      responseId,
      participantId,
      conditions: conditions === null ? null : conditions.split(',').filter(Boolean)
    };
    mailTemplatesApi.preview(code, body, asked, controller.signal).then(
      (answer) => {
        if (controller.signal.aborted) return;
        setState({ shown: answer, problems: {}, other: null, pending: false });
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        if (isInvalidBody(error)) {
          const { byField, other } = problemsOf(error, keys);
          setState((now) => ({ ...now, problems: byField, other, pending: false }));
        } else {
          setState((now) => ({
            ...now,
            problems: {},
            other: refusalMessage(error, 'The preview could not be rendered.'),
            pending: false
          }));
        }
      }
    );
    return () => controller.abort();
  }, [code, survey, body, role, attached, empty, responseId, participantId, conditions]); // eslint-disable-line react-hooks/exhaustive-deps
  return state;
}

export function hasPreviewProblems(state: IPreviewState): boolean {
  return Object.keys(state.problems).length > 0 || state.other !== null;
}

/** A small set of choices as one row of buttons: `radiogroup`, arrows move the choice. */
export function Segmented<T extends string>({
  label,
  value,
  onChange,
  choices
}: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  choices: readonly { value: T; label: string }[];
}) {
  const buttons = React.useRef<(HTMLButtonElement | null)[]>([]);
  const move = (event: React.KeyboardEvent, index: number) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const next = (index + step + choices.length) % choices.length;
    onChange(choices[next].value);
    buttons.current[next]?.focus();
  };
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="inline-flex h-8 items-stretch rounded-md border border-border bg-[var(--color-slight-blue,#f3f6fa)] p-0.5"
    >
      {choices.map((choice, index) => {
        const checked = choice.value === value;
        return (
          <button
            key={choice.value}
            ref={(element) => {
              buttons.current[index] = element;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            onClick={() => onChange(choice.value)}
            onKeyDown={(event) => move(event, index)}
            className={cn(
              'rounded px-2 text-xs font-medium whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-ring',
              checked
                ? 'bg-white text-[var(--color-royal-blue)] shadow-sm'
                : 'text-muted-foreground hover:text-[var(--color-royal-blue)]'
            )}
          >
            {choice.label}
          </button>
        );
      })}
    </div>
  );
}

export function PreviewToolbar({
  template,
  options,
  onChange
}: {
  template: IMailTemplate;
  options: IPreviewOptions;
  onChange: (options: IPreviewOptions) => void;
}) {
  const roleId = React.useId();
  const can = useCan();
  const set = (patch: Partial<IPreviewOptions>) => onChange({ ...options, ...patch });
  const named = (name: string) => template.conditions.some((condition) => condition.name === name);
  const roles = Object.values(ROLE_CONDITIONS).some(named);
  const pdf = named('pdf_attached');
  // A response's values fill a survey mail, a list participant's a custom template's; an account
  // mail has none to pick from.
  const staff = template.group === 'custom';
  const samples = (template.group === 'survey' || staff) && can('responses.browse');
  const sampleName = template.placeholders.find((placeholder) => placeholder.name === 'participant')?.sample ?? null;
  return (
    <div className="grid gap-2 text-[0.8125rem]">
      {(samples || roles) && (
        <div className="flex flex-wrap items-center gap-2">
          {samples && (
            <SamplePicker
              code={template.code}
              value={options.sample}
              onChange={(sample) => set({ sample })}
              sampleName={sampleName}
              participants={staff}
            />
          )}
          {roles && (
            <span className="flex min-w-0 flex-[2_1_12rem] items-center gap-2">
              <span id={roleId} className="text-xs whitespace-nowrap text-muted-foreground">
                Copy for
              </span>
              <ChoiceSelect
                labelledBy={roleId}
                value={options.role}
                onChange={(role) => set({ role: role as MailPreviewRole, conditions: null })}
                choices={PREVIEW_ROLES}
                className="h-8 min-w-0 flex-1"
              />
            </span>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          label="Width"
          value={options.device}
          onChange={(device) => set({ device })}
          choices={[
            { value: 'desktop', label: 'Desktop' },
            { value: 'mobile', label: 'Mobile' }
          ]}
        />
        <Segmented
          label="Format"
          value={options.format}
          onChange={(format) => set({ format })}
          choices={[
            { value: 'html', label: 'HTML' },
            { value: 'text', label: 'Plain text' }
          ]}
        />
        {pdf && (
          <Segmented
            label="PDF"
            value={options.attached ? 'attached' : 'missing'}
            onChange={(choice) => set({ attached: choice === 'attached', conditions: null })}
            choices={[
              { value: 'attached', label: 'PDF attached' },
              { value: 'missing', label: 'No PDF' }
            ]}
          />
        )}
        <button
          type="button"
          aria-pressed={options.empty}
          onClick={() => set({ empty: !options.empty })}
          className={toggleClass(options.empty)}
        >
          Empty values
        </button>
        {template.conditions.length > 0 && (
          <ConditionsMenu conditions={template.conditions} options={options} onChange={onChange} />
        )}
      </div>
    </div>
  );
}

function toggleClass(on: boolean): string {
  return cn(
    'inline-flex h-8 items-center gap-1 rounded-md border px-2 text-xs font-medium whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-ring',
    on
      ? 'border-[var(--color-teal)] bg-[color-mix(in_srgb,var(--color-turquoise)_14%,white)] text-[var(--color-royal-blue)]'
      : 'border-border bg-[var(--color-slight-blue,#f3f6fa)] text-muted-foreground hover:text-[var(--color-royal-blue)]'
  );
}

/** Which of the template's conditions the preview shows as holding — each one switchable. */
function ConditionsMenu({
  conditions,
  options,
  onChange
}: {
  conditions: readonly IMailCondition[];
  options: IPreviewOptions;
  onChange: (options: IPreviewOptions) => void;
}) {
  const on = conditionsOn(conditions, options);
  // Without a recipient's or the PDF's condition, every condition starts at its sample.
  const follows = conditions.some(({ name }) => name === 'pdf_attached' || Object.values(ROLE_CONDITIONS).includes(name));
  const automatic = follows ? 'Following the recipient and the PDF' : 'At their samples';
  const toggle = (name: string, checked: boolean) => {
    const next = new Set(on);
    if (checked) next.add(name);
    else next.delete(name);
    onChange({ ...options, conditions: conditions.map(({ name: each }) => each).filter((each) => next.has(each)) });
  };
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button type="button" className={toggleClass(options.conditions !== null)} data-preview-conditions="">
          Conditions · {on.size} on
          <ChevronDown className="size-3.5" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 text-[0.8125rem]">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {options.conditions ? 'Set by hand' : automatic}
        </DropdownMenuLabel>
        {conditions.map((condition) => (
          <DropdownMenuCheckboxItem
            key={condition.name}
            checked={on.has(condition.name)}
            onCheckedChange={(checked) => toggle(condition.name, checked === true)}
            onSelect={(event) => event.preventDefault()}
            aria-labelledby={`preview-condition-${condition.name}`}
          >
            <span className="grid">
              <span id={`preview-condition-${condition.name}`}>{conditionLabel(condition.name)}</span>
              <span className="text-xs text-muted-foreground">{condition.description}</span>
            </span>
          </DropdownMenuCheckboxItem>
        ))}
        {options.conditions && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onChange({ ...options, conditions: null })}>
              {follows ? 'Follow the recipient and the PDF' : 'Back to the samples'}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The preview itself under its subject line, or why there is none. `onPick` links it to the
 *  fields; `fill` lets the frame take the height left in a sticky panel. */
export function PreviewBody({
  state,
  options,
  focus = null,
  onPick,
  fill = false,
  blocked = 'No preview until the problems are fixed.',
  sender = ''
}: {
  state: IPreviewState;
  options: IPreviewOptions;
  focus?: Nullable<string>;
  onPick?: (field: string) => void;
  fill?: boolean;
  /** Said when survey-api refuses the fields and there is no earlier preview to dim. */
  blocked?: string;
  /** The sender's name the inbox shows; "" for the default. */
  sender?: string;
}) {
  const stale = hasPreviewProblems(state);
  const height = fill ? 'h-[720px] lg:h-auto lg:min-h-[22rem] lg:flex-1' : 'h-[720px]';
  return (
    <div
      className={cn('flex min-h-0 flex-col gap-3 transition-opacity', fill && 'flex-1', stale && 'opacity-50')}
      aria-busy={state.pending || undefined}
      data-preview-stale={stale || undefined}
    >
      {state.other && (
        <p className="text-sm text-destructive" role="alert">
          {state.other}
        </p>
      )}
      {state.shown ? (
        <>
          <InboxRow
            preview={state.shown}
            sender={sender.trim() || SENDER}
            mobile={options.device === 'mobile'}
            focus={focus}
            onPick={onPick}
          />
          {options.format === 'text' ? (
            <pre
              data-preview-text=""
              className={cn(
                'overflow-auto rounded-md border border-border bg-white p-4 font-mono text-xs leading-relaxed whitespace-pre-wrap',
                height
              )}
            >
              {state.shown.text}
            </pre>
          ) : (
            <div
              className={cn(
                'flex min-h-0 justify-center rounded-md border border-border',
                options.device === 'mobile' && 'bg-[color-mix(in_srgb,var(--color-slight-blue)_40%,white)] py-3',
                height
              )}
            >
              <PreviewFrame
                html={state.shown.html}
                focus={focus}
                onPick={onPick}
                className={cn(
                  'h-full bg-white',
                  options.device === 'mobile'
                    ? 'w-[375px] max-w-full rounded-md border border-border shadow-sm'
                    : 'w-full rounded-md'
                )}
              />
            </div>
          )}
        </>
      ) : stale ? (
        !state.other && (
          <p className="text-sm text-muted-foreground">{blocked}</p>
        )
      ) : (
        <TableSkeleton rows={4} />
      )}
    </div>
  );
}

/** The sender an inbox shows when the template names none. */
const SENDER = 'Veritant';

/** The mail as an inbox lists it — sender, subject, then the preview text in grey — one row on a
 *  desktop, three lines on a phone. The subject and the preview text are linked to their fields. */
function InboxRow({
  preview,
  sender,
  mobile,
  focus,
  onPick
}: {
  preview: IMailPreview;
  sender: string;
  mobile: boolean;
  focus: Nullable<string>;
  onPick?: (field: string) => void;
}) {
  const part = (field: 'subject' | 'preheader') =>
    cn(
      'rounded-sm transition-colors',
      onPick && 'cursor-pointer hover:bg-[color-mix(in_srgb,var(--color-turquoise)_10%,white)]',
      // Inset, so the row's truncation does not clip it.
      focus === field &&
        'bg-[color-mix(in_srgb,var(--color-turquoise)_14%,white)] shadow-[inset_0_0_0_2px_var(--color-teal)]'
    );
  const subject = (
    <span className={cn(part('subject'), 'font-medium')} onClick={onPick ? () => onPick('subject') : undefined} data-preview-subject>
      {preview.subject}
    </span>
  );
  const preheader = preview.preheader && (
    <span className={cn(part('preheader'), 'text-muted-foreground')} onClick={onPick ? () => onPick('preheader') : undefined} data-preview-preheader>
      {preview.preheader}
    </span>
  );
  return (
    <div
      aria-label="In the inbox"
      role="group"
      data-preview-inbox=""
      className={cn(
        'rounded-md border border-border bg-[color-mix(in_srgb,var(--color-slight-blue)_35%,white)] text-[0.8125rem]',
        mobile ? 'mx-auto grid w-[375px] max-w-full gap-0.5 px-3 py-2' : 'flex min-w-0 items-baseline gap-4 px-3 py-2'
      )}
    >
      <span className="shrink-0 font-semibold">{sender}</span>
      {mobile ? (
        <>
          <span className="truncate">{subject}</span>
          {preheader && <span className="truncate">{preheader}</span>}
        </>
      ) : (
        <span className="min-w-0 flex-1 truncate">
          {subject}
          {preheader && <span className="text-muted-foreground"> — </span>}
          {preheader}
        </span>
      )}
    </div>
  );
}

/** What `onPick` is handed for a portal block clicked in the preview: `block:<id>`. */
export const BLOCK_PICK = 'block:';

/** Outlines for the linked regions — this page's teal and turquoise. */
const PART_STYLE = [
  'body[data-linked] [data-part]{cursor:pointer;border-radius:3px;outline:2px solid transparent;',
  'outline-offset:3px;transition:outline-color .15s,background-color .15s}',
  'body[data-linked] [data-part]:hover{outline-color:rgba(18,121,124,.35)}',
  'body[data-linked] [data-system-block]{cursor:pointer;border-radius:3px;outline:1px dashed transparent;outline-offset:2px}',
  'body[data-linked] [data-system-block]:hover{outline-color:#12797c}',
  '[data-part][data-focused]{outline-color:#12797c!important;background-color:rgba(2,201,181,.13)!important}'
].join('');

/** The body fragment as a document of its own, on white. */
function previewDocument(html: string, linked: boolean): string {
  return (
    `<!doctype html><html><head><meta charset="utf-8"><style>${PART_STYLE}</style></head>` +
    `<body style="margin:0;background:#fff"${linked ? ' data-linked' : ''}>${html}</body></html>`
  );
}

function PreviewFrame({
  html,
  focus,
  onPick,
  className
}: {
  html: string;
  focus: Nullable<string>;
  onPick?: (field: string) => void;
  className?: string;
}) {
  const frame = React.useRef<HTMLIFrameElement>(null);
  const latest = React.useRef({ focus, onPick });
  latest.current = { focus, onPick };
  const linked = Boolean(onPick);
  const srcDoc = React.useMemo(() => previewDocument(html, linked), [html, linked]);

  // The focused field's region outlined — and scrolled to, when focus moved there.
  const mark = React.useCallback((scroll: boolean) => {
    const doc = frame.current?.contentDocument;
    const view = doc?.defaultView;
    if (!doc?.body || !view) return;
    const part = latest.current.focus;
    const regions = Array.from(doc.querySelectorAll('[data-part]'));
    for (const region of regions) region.toggleAttribute('data-focused', region.getAttribute('data-part') === part);
    const region = regions.find((each) => each.getAttribute('data-part') === part) ?? null;
    if (!scroll || !region) return;
    const box = region.getBoundingClientRect();
    if (box.top < 0 || box.bottom > view.innerHeight) {
      view.scrollTo({ top: view.scrollY + box.top - 24, behavior: 'smooth' });
    }
  }, []);

  React.useEffect(() => mark(true), [focus, mark]);

  const loaded = () => {
    const doc = frame.current?.contentDocument;
    if (!doc) return;
    doc.addEventListener('click', (event) => {
      // Nothing in the preview navigates.
      event.preventDefault();
      const target = event.target as Nullable<Element>;
      // A portal block picks its card in the body; anything else its field.
      const block = target?.closest?.('[data-system-block]')?.getAttribute('data-system-block');
      const part = target?.closest?.('[data-part]')?.getAttribute('data-part');
      if (block) latest.current.onPick?.(`${BLOCK_PICK}${block}`);
      else if (part) latest.current.onPick?.(part);
    });
    mark(false);
  };

  return (
    <iframe
      ref={frame}
      title="Preview of the email"
      sandbox="allow-same-origin"
      srcDoc={srcDoc}
      onLoad={loaded}
      className={className}
    />
  );
}
